import { Reveal } from '@components/motion';
import { ABOUT } from '@/data/about';
import FigmaFrame from '@components/landing/FigmaFrame';
import { Img } from '@/components/Img';

const QUOTE_ICON = '/about/quote-icon.svg';
const AVATAR = '/about/intro-avatar.webp';

/** The pull quote, run by run — the CMS marks which runs go grey. */
function Quote({ className }: { className?: string }) {
    return (
        <p className={className}>
            {ABOUT.who.quote.map((run, i) =>
                run.grey ? (
                    <span key={i} className="text-[#a0a3a5]">
                        {run.text}
                    </span>
                ) : (
                    <span key={i}>{run.text}</span>
                ),
            )}
        </p>
    );
}

function Author({ size = 'desktop' }: { size?: 'desktop' | 'mobile' }) {
    const avatar = size === 'desktop' ? 'size-[85px]' : 'size-[60px]';
    return (
        <div className="flex items-center gap-[16px]">
            <div className="h-px w-[119px] shrink-0 bg-[#282828]/60" />
            <div className="flex items-center gap-[22px]">
                <Img
                    src={ABOUT.who.authorAvatar || AVATAR}
                    alt={ABOUT.who.authorName}
                    className={`${avatar} shrink-0 rounded-full object-cover`}
                />
                <div className="whitespace-nowrap">
                    <p className="text-[20px] font-bold leading-[28px] text-black">{ABOUT.who.authorName}</p>
                    <p className="text-[16px] font-normal leading-[24px] text-[#595d62]">{ABOUT.who.authorRole}</p>
                </div>
            </div>
        </div>
    );
}

function IntroDesktop() {
    return (
        <FigmaFrame designWidth={1920} designHeight={1282} className="w-full">
            <div className="relative h-[1282px] w-[1920px] bg-[#f5f5f5]">
                <div className="absolute left-[153px] top-[171px] w-[1629px]">
                    {/* Heading + right body */}
                    <div className="relative h-[185px]">
                        <Reveal
                            as="div"
                            once
                            variant="fade-up"
                            className="absolute left-0 top-0 w-[606px] text-[48px] font-bold leading-[61px] tracking-[-1px] text-black"
                        >
                            <p className="mb-0">{ABOUT.who.heading}</p>
                            <p className="font-medium">
                                <span className="text-[#b6b6b6]">{ABOUT.who.highlight}</span>
                                <span>.</span>
                            </p>
                        </Reveal>
                        <Reveal
                            as="div"
                            once
                            variant="fade-up"
                            delay={150}
                            className="absolute left-[723px] top-[15px] w-[906px] text-[16px] font-medium leading-[1.8] text-[#282828]"
                        >
                            <p className="mb-0">{ABOUT.who.body1}</p>
                            <p>{ABOUT.who.body2}</p>
                        </Reveal>
                    </div>

                    {/* Featured quotation */}
                    <div className="absolute left-[260px] top-[343px] flex w-[1093px] flex-col gap-[80px]">
                        <Reveal as="div" once variant="fade-up" className="relative">
                            <Img src={QUOTE_ICON} alt="" className="absolute left-0 top-0 size-[130px]" />
                            <Quote className="ml-[138px] mt-[48.5px] w-[959px] font-primary text-[80px] font-medium leading-[1.2] tracking-[-1.6px] text-[#08080c]" />
                        </Reveal>
                        <Reveal as="div" once variant="fade-up" delay={150}>
                            <Author />
                        </Reveal>
                    </div>
                </div>
            </div>
        </FigmaFrame>
    );
}

function IntroMobile() {
    return (
        <section className="bg-[#f5f5f5] px-5 py-16">
            <h2 className="text-[32px] font-bold leading-[40px] tracking-[-1px] text-black">
                About Us Citizens’ Gavel.
                <span className="block font-medium text-[#b6b6b6]">The delay is the injustice.</span>
            </h2>
            <div className="mt-6 space-y-4 text-[14px] font-medium leading-[1.8] text-[#282828]">
                <p>{ABOUT.who.body1}</p>
                <p>{ABOUT.who.body2}</p>
            </div>

            <div className="mt-12">
                <Img src={QUOTE_ICON} alt="" className="size-[64px]" />
                <Quote className="mt-4 font-primary text-[28px] font-medium leading-[1.25] tracking-[-0.5px] text-[#08080c]" />
                <div className="mt-8">
                    <Author size="mobile" />
                </div>
            </div>
        </section>
    );
}

export default function AboutIntro() {
    return (
        <>
            <div className="hidden lg:block">
                <IntroDesktop />
            </div>
            <div className="lg:hidden">
                <IntroMobile />
            </div>
        </>
    );
}
